import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  GetAllMemberAndGuarantor,
  GetMemberAndGuarntor,
  fetchVenmoPaymentSummary,
  fetchVenmoCandidates,
  saveVenmoMemberMappings,
  sendEmailToTenants,
  fetchGroupOccupantsForArh,
  fetchUnresolvedAppfolioGroupSuggestions,
  linkUnresolvedAppfolioGroup,
} from "./DataServices/ProspectiveTenantDataService";
import {
  AutoResponseDetailsinfo,
  IPublicTenantLead,
  MappingItem,
  IVenmoCandidate,
} from "./Models";
import {
  getTenantMappings,
  saveTenantMappings,
} from "./DataServices/ReminderService";
import EmailModal from "./EmailModal";
import { fetchPublicTenantLeads } from "./DataServices/PublicTenantLeadsDataService";
import { baseUrl } from "./DataServices/Configuration";

// A search candidate from either source, tagged so handleSelectMapping/handleSaveAllMappings
// know whether to write appfolioId/mappedTo or groupOccupantId/mappedToGroupOccupant.
// Source-2 (PublicGroupOccupant) entries are projected into the same IPublicTenantLead
// shape (apartmentTitle -> apartmentName, isCosigner -> isGuarantor, phone -> phoneNo) so
// the rest of this component's render/search logic doesn't need to special-case it.
interface SearchableAppfolioCandidate extends IPublicTenantLead {
  source: "publicLead" | "groupOccupant";
}

// Same real applicant can be scraped by both AppFolio sources independently.
// Identity key prefers email (more reliable); falls back to normalized name.
// Pure/module-level so it's stable across renders (no exhaustive-deps churn).
const appfolioIdentityKey = (name?: string, email?: string) => {
  const normalizedEmail = (email || "").trim().toLowerCase();
  if (normalizedEmail) return `email:${normalizedEmail}`;
  const normalizedName = (name || "")
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return `name:${normalizedName}`;
};

interface TenantMappingProps {
  arhid: string;
  appfolioMembers?: IPublicTenantLead[];
  memberGuarantorInfo?: AutoResponseDetailsinfo;
  onMappingsUpdated?: () => Promise<void> | void;
  prospectiveTenant?: { name: string; email: string; phone?: string };
  calculateMappedMembers?: (
    info: AutoResponseDetailsinfo,
    appfolioData: IPublicTenantLead[],
    apartmentName: string,
    inquiryPerson: any,
    publicLeads: any[],
  ) => number;
  isNameMatch: (
    memberFirstName: string,
    memberLastName: string,
    compareName: string,
  ) => { isMatch: boolean; confidence: number };
  getStringSimilarity?: (str1: string, str2: string) => number;
  getEditDistance?: (str1: string, str2: string) => number;
  normalizeName?: (name: string) => string;
  tenantEmail?: string;
  tenantName?: string;
  apartmentName?: string;
  // When true, renders an info-only view: no search/select/clear/save/email
  // controls, just mapping status and badges.
  readOnly?: boolean;
  // When true (with readOnly), renders a condensed single-line-per-member
  // list instead of the full member cards.
  compact?: boolean;
}

const TenantMapping: React.FC<TenantMappingProps> = ({
  arhid,
  appfolioMembers,
  memberGuarantorInfo,
  onMappingsUpdated,
  prospectiveTenant,
  calculateMappedMembers,
  isNameMatch,
  getStringSimilarity,
  getEditDistance,
  normalizeName,
  tenantEmail,
  tenantName,
  apartmentName,
  readOnly = false,
  compact = false,
}) => {
  const [systemMembers, setSystemMembers] = useState<MappingItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<{ [key: string]: string }>({});
  const [filteredAppfolioData, setFilteredAppfolioData] = useState<{
    [key: string]: SearchableAppfolioCandidate[];
  }>({});
  const [venmoCandidates, setVenmoCandidates] = useState<IVenmoCandidate[]>(
    [],
  );
  const [venmoSearchQuery, setVenmoSearchQuery] = useState<{
    [key: string]: string;
  }>({});
  const [filteredVenmoCandidates, setFilteredVenmoCandidates] = useState<{
    [key: string]: IVenmoCandidate[];
  }>({});
  const [venmoConfirming, setVenmoConfirming] = useState<{
    [key: string]: boolean;
  }>({});
  const [publicLeadCandidates, setPublicLeadCandidates] = useState<
    SearchableAppfolioCandidate[]
  >([]);
  // Source-2 (group-aware AppFolio scrape) candidates for this ARH's listing.
  const [groupOccupantCandidates, setGroupOccupantCandidates] = useState<
    SearchableAppfolioCandidate[]
  >([]);
  // Unresolved PublicApplicationGroups whose occupant names fuzzy-match this ARH's
  // roster — surfaced as a manual "link this group" recovery suggestion.
  const [unresolvedGroupSuggestions, setUnresolvedGroupSuggestions] = useState<
    any[]
  >([]);
  const [linkingGroupId, setLinkingGroupId] = useState<string | null>(null);
  const [groupFetchTrigger, setGroupFetchTrigger] = useState(0);
  const normalizeNameStr = (name?: string) => {
    return (
      name
        ?.toLowerCase()
        .replace(/[^\w\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim() || ""
    );
  };

  // Unified search pool: Source 1 + Source 2, tagged by source. Source 1 already wins
  // everywhere else (auto-mapping), so a Source-2 entry that's really the same person as
  // an already-listed Source-1 lead is dropped here too — otherwise the same applicant
  // shows up twice in search results with no way to tell they're one person.
  const MemberGuarantorInfosFromAppfolio: SearchableAppfolioCandidate[] =
    useMemo(() => {
      const seenIdentities = new Set(
        publicLeadCandidates.map((c) => appfolioIdentityKey(c.name, c.email)),
      );
      const dedupedGroupOccupants = groupOccupantCandidates.filter((c) => {
        const key = appfolioIdentityKey(c.name, c.email);
        if (seenIdentities.has(key)) return false;
        seenIdentities.add(key);
        return true;
      });
      return [...publicLeadCandidates, ...dedupedGroupOccupants];
    }, [publicLeadCandidates, groupOccupantCandidates]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [hasExistingMappings, setHasExistingMappings] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"success" | "error" | null>(
    null,
  );
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState<"success" | "error" | null>(
    null,
  );
  const [publicLeads, setPublicLeads] = useState<any[]>([]);
  const [isFormFilled, setIsFormFilled] = useState(false);
  const [venmoPayments, setVenmoPayments] = useState<
    {
      payerName?: string;
      amount?: number;
      paymentDate?: string;
      transactionId?: string;
    }[]
  >([]);

  useEffect(() => {
    if (!arhid) return;
    fetchVenmoPaymentSummary(arhid).then((res) => {
      setVenmoPayments(res?.payments || []);
    });
  }, [arhid]);

  useEffect(() => {
    if (!arhid) return;
    fetchVenmoCandidates(arhid).then((res) => {
      setVenmoCandidates(Array.isArray(res) ? res : []);
    });
  }, [arhid]);

  useEffect(() => {
    const fetchPublicLeadsData = async () => {
      try {
        const data = await fetchPublicTenantLeads();
        setPublicLeads(data || []);
      } catch (error) {
        console.error("Error fetching public leads:", error);
        setPublicLeads([]);
      }
    };
    fetchPublicLeadsData();
  }, []);

  useEffect(() => {
    const fetchdata = async () => {
      const data = await GetMemberAndGuarntor(arhid);
      setSystemMembers(data);
    };
    fetchdata();
  }, [arhid]);
  // Add refs to maintain focus
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const venmoInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>(
    {},
  );
  const mappingRowRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Close any open Appfolio/Venmo suggestion dropdowns when clicking outside their row
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      Object.keys(mappingRowRefs.current).forEach((id) => {
        const rowEl = mappingRowRefs.current[id];
        if (rowEl && !rowEl.contains(e.target as Node)) {
          setFilteredAppfolioData((prev) =>
            prev[id]?.length ? { ...prev, [id]: [] } : prev,
          );
          setFilteredVenmoCandidates((prev) =>
            prev[id]?.length ? { ...prev, [id]: [] } : prev,
          );
        }
      });
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch Lead data (Source 1)
  useEffect(() => {
    const tagAsPublicLead = (
      leads: IPublicTenantLead[],
    ): SearchableAppfolioCandidate[] =>
      leads.map((l) => ({ ...l, source: "publicLead" as const }));

    if (appfolioMembers) {
      setPublicLeadCandidates(tagAsPublicLead(appfolioMembers));
    } else {
      const fetchData = async () => {
        try {
          const data = await fetchPublicTenantLeads();
          setPublicLeadCandidates(tagAsPublicLead(data));
        } catch (error) {
          console.error("Error fetching Public leads data:", error);
          setPublicLeadCandidates([]);
        }
      };
      fetchData();
    }
  }, [appfolioMembers]);

  // Fetch Source-2 (group-aware AppFolio scrape) candidates for this ARH's listing —
  // mirrors the fetchVenmoCandidates(arhid) effect above.
  useEffect(() => {
    if (!arhid) return;
    let cancelled = false;
    (async () => {
      try {
        const occupants = await fetchGroupOccupantsForArh(arhid);
        if (cancelled) return;
        const mapped: SearchableAppfolioCandidate[] = (occupants || [])
          .filter((o) => o.name)
          .map((o) => ({
            id: o.id,
            name: o.name,
            email: o.email,
            apartmentName: o.apartmentTitle,
            phoneNo: o.phone || "",
            createdAtUtc: o.createdAtUtc,
            isGuarantor: o.isCosigner,
            source: "groupOccupant" as const,
          }));
        setGroupOccupantCandidates(mapped);
      } catch (error) {
        if (!cancelled) setGroupOccupantCandidates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [arhid, groupFetchTrigger]);

  // Unresolved AppFolio groups that look like they belong to this ARH (name match),
  // but never got auto-attached to a listing.
  useEffect(() => {
    if (!arhid) return;
    let cancelled = false;
    (async () => {
      try {
        const suggestions = await fetchUnresolvedAppfolioGroupSuggestions(arhid);
        if (!cancelled) {
          setUnresolvedGroupSuggestions(
            Array.isArray(suggestions) ? suggestions : [],
          );
        }
      } catch (error) {
        if (!cancelled) setUnresolvedGroupSuggestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [arhid, groupFetchTrigger]);

  const handleLinkUnresolvedGroup = async (groupId: string) => {
    setLinkingGroupId(groupId);
    try {
      await linkUnresolvedAppfolioGroup(groupId, arhid);
      setGroupFetchTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error linking AppFolio group:", error);
    } finally {
      setLinkingGroupId(null);
    }
  };

  // Main mapping logic with proper 3-layer matching:
  // memberGuarantorInfo.email → publicTenantLeads.email → appfolioData
  useEffect(() => {
    const fetchAndMergeMappings = async () => {
      if (!arhid) return;

      setLoading(true);
      try {
        // LAYER 1: Get current member/guarantor info from system
        const memberInfo: AutoResponseDetailsinfo[] =
          await GetAllMemberAndGuarantor(arhid, false);

        const currentMembers = memberInfo[0]?.memberAndGaurantorInfo || [];
        setIsFormFilled(currentMembers.length > 0);

        // Get existing mappings from DB
        const existingMappings = await getTenantMappings(arhid);

        // Create a map of existing mappings
        const existingMappingsMap = new Map<string, MappingItem>();

        if (existingMappings && existingMappings.length > 0) {
          existingMappings.forEach((mapping: MappingItem) => {
            existingMappingsMap.set(mapping.id, mapping);
          });
          setHasExistingMappings(true);
        } else {
          setHasExistingMappings(false);
        }

        // Normalize apartment name for filtering
        const normalizeApt = (name?: string) => {
          if (!name) return { stripped: "", digits: "" };
          const lower = name.toLowerCase();
          const stripped = lower
            .replace(/apt|#|unit|room|flat/g, "")
            .replace(/\s+/g, "")
            .trim();
          const digits = lower.match(/\d+/g)?.join("") || "";
          return { stripped, digits };
        };
        // Process all members using ONLY DB mappings (no frontend auto-matching)
        let mergedData = currentMembers.map((member: any) => {
          const existingMapping = existingMappingsMap.get(member.id);

          const emailValue =
            member.email ||
            member.Email ||
            member.emailAddress ||
            member.EmailAddress ||
            member["Email"] ||
            "";

          const memberEmail = emailValue;

          if (existingMapping) {
            return {
              ...existingMapping,
              name: member.name,
              email:
                memberEmail ||
                existingMapping.email ||
                existingMapping.mappedTo?.email ||
                "",
              isGuarantor: member.isGuarantor,
              arhId: arhid,
              appfolioId:
                existingMapping.mappedTo?.id ||
                existingMapping.appfolioId ||
                "",
              mappedTo: existingMapping.mappedTo || null,
              mappedToGroupOccupant: existingMapping.mappedToGroupOccupant || null,
              isConfirmed: existingMapping.isConfirmed,
              isManuallyConfirmed:
                member.isManuallyConfirmed ??
                existingMapping.isManuallyConfirmed ??
                false,
              autoMapped:
                existingMapping.mappedTo || existingMapping.mappedToGroupOccupant
                  ? true
                  : false,
            };
          } else {
            return {
              ...member,
              arhId: arhid,
              email: memberEmail,
              appfolioId: "",
              mappedTo: null,
              isConfirmed: false,
              isManuallyConfirmed: member.isManuallyConfirmed ?? false,
              autoMapped: false,
              isDeleted: false,
            };
          }
        });

        // Add or update Inquiry Person
        let inquiryPersonInList = mergedData.find(
          (m) =>
            m.id === arhid ||
            (m.email &&
              m.email.toLowerCase().trim() ===
                tenantEmail?.toLowerCase().trim()) ||
            normalizeNameStr(m.name) === normalizeNameStr(tenantName || ""),
        );

        if (!inquiryPersonInList && tenantName) {
          const existingMapping = existingMappingsMap.get(arhid);
          const finalInquiryEmail =
            tenantEmail ||
            existingMapping?.email ||
            existingMapping?.mappedTo?.email ||
            "";
          const inquiryAppfolioId =
            existingMapping?.mappedTo?.id || existingMapping?.appfolioId || "";

          mergedData = [
            {
              id: arhid,
              name: tenantName,
              email: finalInquiryEmail,
              isGuarantor: false,
              arhId: arhid,
              appfolioId: inquiryAppfolioId,
              mappedTo: existingMapping?.mappedTo || null,
              isConfirmed: existingMapping?.isConfirmed ?? false,
              isManuallyConfirmed: existingMapping?.isManuallyConfirmed ?? false,
              autoMapped: existingMapping ? true : false,
              isDeleted: false,
              isInquiryPerson: true,
            },
            ...mergedData,
          ];
        } else if (inquiryPersonInList) {
          inquiryPersonInList.isInquiryPerson = true;
          if (!inquiryPersonInList.email) {
            inquiryPersonInList.email = tenantEmail || "";
          }
        }

        setSystemMembers(mergedData);
      } catch (error) {
        console.error("Error in mapping logic:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAndMergeMappings();
  }, [
    arhid,
    MemberGuarantorInfosFromAppfolio,
    isNameMatch,
    publicLeads,
    tenantEmail,
    tenantName,
    apartmentName,
    getStringSimilarity,
  ]);

  // A member is mapped via either source — the legacy flat PublicTenantLead (mappedTo)
  // or the group-aware AppFolio scrape (mappedToGroupOccupant).
  const isMapped = (member: MappingItem) =>
    !!member.mappedTo || !!member.mappedToGroupOccupant;

  const getMappingStatus = () => {
    const totalMembers = systemMembers.length;
    const mappedMembers = systemMembers.filter(isMapped).length;
    const unmappedMembers = systemMembers.filter((m) => !isMapped(m)).length;

    return {
      total: totalMembers,
      mapped: mappedMembers,
      confirmed: mappedMembers,
      unmapped: unmappedMembers,
      hasUnmappedEntries: unmappedMembers > 0,
    };
  };

  const handleSendEmail = async (emailData: {
    to: string;
    subject: string;
    body: string;
  }) => {
    if (!emailData.to || !emailData.subject || !emailData.body) {
      setEmailStatus("error");
      return;
    }

    setEmailSending(true);
    try {
      const emailRequest = {
        keypairOfToAndSuject: [
          {
            email: emailData.to,
            subject: emailData.subject,
          },
        ],
        cc: [],
        body: emailData.body,
        attachments: [],
      };

      await sendEmailToTenants(emailRequest);

      setEmailStatus("success");
      setIsEmailModalOpen(false);
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailStatus("error");
    } finally {
      setEmailSending(false);

      setTimeout(() => {
        setEmailStatus(null);
      }, 3000);
    }
  };

  const shouldEnableSendEmail = () => {
    const hasUnmappedMembers = systemMembers.some((member) => !isMapped(member));
    return hasUnmappedMembers;
  };

  const getEmailData = () => {
    const unmappedMembers = systemMembers.filter((member) => !isMapped(member));
    const mappedMembers = systemMembers.filter((member) => isMapped(member));

    return { unmappedMembers, mappedMembers };
  };

  const handleSearch = (memberId: string, query: string) => {
    setSearchQuery((prev) => ({ ...prev, [memberId]: query }));

    const member = systemMembers.find((m) => m.id === memberId);
    if (!member) return;

    const normalizeApt = (name?: string) => {
      if (!name) return { stripped: "", digits: "" };
      const lower = name.toLowerCase();
      const stripped = lower
        .replace(/apt|#|unit|room|flat/g, "")
        .replace(/\s+/g, "")
        .trim();
      const digits = lower.match(/\d+/g)?.join("") || "";
      return { stripped, digits };
    };

    const targetApt = normalizeApt(apartmentName || "");
    const apartmentFilteredData = targetApt.stripped
      ? MemberGuarantorInfosFromAppfolio.filter((ap) => {
          const apApt = normalizeApt(ap.apartmentName || "");
          const strippedMatch =
            apApt.stripped.includes(targetApt.stripped) ||
            targetApt.stripped.includes(apApt.stripped);
          const digitsMatch =
            targetApt.digits &&
            apApt.digits &&
            (apApt.digits.includes(targetApt.digits) ||
              targetApt.digits.includes(apApt.digits));
          return strippedMatch || digitsMatch;
        })
      : MemberGuarantorInfosFromAppfolio;

    const dataToFilter =
      apartmentFilteredData.length > 0 || !query.trim()
        ? apartmentFilteredData
        : MemberGuarantorInfosFromAppfolio;

    const filtered = dataToFilter
      .filter((appfolioMember) => {
        const alreadyMapped = systemMembers.some(
          (m) =>
            m.id !== memberId &&
            (m.mappedTo?.id === appfolioMember.id ||
              m.mappedToGroupOccupant?.id === appfolioMember.id),
        );
        if (alreadyMapped) return false;

        const sourceName = appfolioMember.name;

        if (!query.trim()) {
          return true;
        }

        const basicMatch = sourceName
          ?.toLowerCase()
          .includes(query.toLowerCase().trim());

        const emailMatch = publicLeads.some(
          (pl) =>
            (pl.email || pl.Email || "")
              .toLowerCase()
              .includes(query.toLowerCase().trim()) &&
            normalizeNameStr(pl.name || pl.Name) ===
              normalizeNameStr(sourceName),
        );

        let enhancedMatch = false;

        if (sourceName && isNameMatch) {
          const nameParts = query.trim().split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";
          enhancedMatch = isNameMatch(firstName, lastName, sourceName).isMatch;
        }

        return basicMatch || emailMatch || enhancedMatch;
      })
      .sort((a, b) => {
        const aName = a.name;
        const bName = b.name;

        if (!query.trim() || !aName || !bName || !isNameMatch) return 0;

        const nameParts = query.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        const aConfidence = isNameMatch(firstName, lastName, aName).confidence;
        const bConfidence = isNameMatch(firstName, lastName, bName).confidence;

        return bConfidence - aConfidence;
      });

    setFilteredAppfolioData((prev) => ({ ...prev, [memberId]: filtered }));

    setTimeout(() => {
      if (inputRefs.current[memberId]) {
        inputRefs.current[memberId]?.focus();
      }
    }, 0);
  };

  const handleSelectMapping = (
    memberId: string,
    appfolioMember: SearchableAppfolioCandidate,
  ) => {
    const isGroupOccupant = appfolioMember.source === "groupOccupant";
    setSystemMembers((prev) =>
      prev.map((member) =>
        member.id === memberId
          ? {
              ...member,
              mappedTo: isGroupOccupant ? null : appfolioMember,
              mappedToGroupOccupant: isGroupOccupant
                ? {
                    id: appfolioMember.id,
                    name: appfolioMember.name,
                    email: appfolioMember.email,
                    phone: appfolioMember.phoneNo,
                    isCosigner: appfolioMember.isGuarantor,
                    apartmentTitle: appfolioMember.apartmentName,
                    createdAtUtc: appfolioMember.createdAtUtc,
                  }
                : null,
              isConfirmed: true,
            }
          : member,
      ),
    );
    setSearchQuery((prev) => ({ ...prev, [memberId]: "" }));
    setFilteredAppfolioData((prev) => ({ ...prev, [memberId]: [] }));
  };

  const handleVenmoSearch = (memberId: string, query: string) => {
    setVenmoSearchQuery((prev) => ({ ...prev, [memberId]: query }));

    const filtered = venmoCandidates.filter((candidate) => {
      // Hide candidates already linked to a different member
      if (candidate.matchedMemberId && candidate.matchedMemberId !== memberId)
        return false;

      if (!query.trim()) return true;
      return candidate.name.toLowerCase().includes(query.toLowerCase().trim());
    });

    setFilteredVenmoCandidates((prev) => ({ ...prev, [memberId]: filtered }));

    setTimeout(() => {
      if (venmoInputRefs.current[memberId]) {
        venmoInputRefs.current[memberId]?.focus();
      }
    }, 0);
  };

  const handleSelectVenmoMapping = async (
    memberId: string,
    candidate: IVenmoCandidate,
  ) => {
    setVenmoConfirming((prev) => ({ ...prev, [memberId]: true }));
    try {
      await saveVenmoMemberMappings(arhid, [
        { memberId, venmoKey: candidate.key },
      ]);
      setSystemMembers((prev) =>
        prev.map((member) =>
          member.id === memberId
            ? { ...member, isManuallyConfirmed: true }
            : member,
        ),
      );
      setVenmoCandidates((prev) =>
        prev.map((c) =>
          c.key === candidate.key ? { ...c, matchedMemberId: memberId } : c,
        ),
      );
      setVenmoSearchQuery((prev) => ({ ...prev, [memberId]: "" }));
      setFilteredVenmoCandidates((prev) => ({ ...prev, [memberId]: [] }));
      await onMappingsUpdated?.();
    } catch (error) {
      console.error("Error confirming via Venmo:", error);
    } finally {
      setVenmoConfirming((prev) => ({ ...prev, [memberId]: false }));
    }
  };

  const _handleConfirmMapping = (memberId: string) => {
    setSystemMembers((prev) =>
      prev.map((member) =>
        member.id === memberId ? { ...member, isConfirmed: true } : member,
      ),
    );
  };

  const handleRemoveMapping = (memberId: string) => {
    setSystemMembers((prev) =>
      prev.map((member) =>
        member.id === memberId
          ? {
              ...member,
              mappedTo: null,
              mappedToGroupOccupant: null,
              isConfirmed: false,
            }
          : member,
      ),
    );
  };

  const handleSaveAllMappings = async () => {
    setSaveLoading(true);
    try {
      const newMembers = systemMembers.filter((m) => m.id.startsWith("temp_"));

      let membersToPersist: any[] = [];

      if (newMembers.length > 0) {
        const newMembersData = newMembers.map((m) => ({
          id: "",
          name: m.name,
          email: m.email,
          isGuarantor: m.isGuarantor,
        }));

        const token = localStorage.getItem("token");
        const newMembersResponse = await fetch(
          `${baseUrl}/api/Reminder/SaveMemberAndGuarntor?arhId=${arhid}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(newMembersData),
          },
        );

        if (!newMembersResponse.ok) {
          throw new Error("Failed to save new members");
        }

        const memberInfo: AutoResponseDetailsinfo[] =
          await GetAllMemberAndGuarantor(arhid, false);
        const updatedMembers = memberInfo[0]?.memberAndGaurantorInfo || [];

        const merged = [
          ...systemMembers.filter((m) => !m.id.startsWith("temp_")),
          ...updatedMembers.map((m) => ({
            ...m,
            arhId: arhid,
            mappedTo: null,
            isConfirmed: false,
            isDeleted: false,
            appfolioId: null,
          })),
        ];

        // Update UI state
        setSystemMembers(merged);

        // Use the merged list for persistence so we don't rely on stale state
        membersToPersist = merged;
      }

      if (!membersToPersist.length) {
        membersToPersist = systemMembers;
      }

      await saveTenantMappings(
        membersToPersist.map((member) => ({
          ...member,
          mappedTo: member.mappedTo ?? null,
        })),
      );

      // Refresh mappings from server to ensure DB values (and generated IDs) are reflected
      try {
        const refreshed = await getTenantMappings(arhid);
        if (Array.isArray(refreshed)) {
          // Build a lookup of mapped results by member id
          const mapById = new Map<string, any>();
          refreshed.forEach((r: any) => mapById.set(r.id, r));

          const updatedSystem = membersToPersist.map((m) => {
            const r = mapById.get(m.id);
            if (r) {
              return {
                ...m,
                mappedTo: r.mappedTo || null,
                isConfirmed: r.isConfirmed ?? !!m.isConfirmed,
              };
            }
            return m;
          });

          setSystemMembers(updatedSystem);
        }
      } catch (err) {
        console.warn("Failed to refresh mappings after save", err);
      }

      await onMappingsUpdated?.();

      setSaveStatus("success");
      setHasExistingMappings(true);
      setIsFormFilled(true);
    } catch (error) {
      console.error("Error saving mappings:", error);
      setSaveStatus("error");
    } finally {
      setSaveLoading(false);

      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    }
  };

  const _handleMemberNameChange = (memberId: string, newName: string) => {
    setSystemMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, name: newName } : m)),
    );
  };

  const _handleMemberEmailChange = (memberId: string, newEmail: string) => {
    setSystemMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, email: newEmail } : m)),
    );
  };

  const _addNewTenant = () => {
    const newMember: MappingItem = {
      id: `temp_${Date.now()}`,
      name: "",
      email: "",
      isGuarantor: false,
      arhId: arhid,
      mappedTo: null,
      isConfirmed: false,
      isDeleted: false,
      appfolioId: null,
    };
    setSystemMembers((prev) => [...prev, newMember]);
  };

  const _addNewGuarantor = () => {
    const newMember: MappingItem = {
      id: `temp_${Date.now()}`,
      name: "",
      email: "",
      isGuarantor: true,
      arhId: arhid,
      mappedTo: null,
      isConfirmed: false,
      isDeleted: false,
      appfolioId: null,
    };
    setSystemMembers((prev) => [...prev, newMember]);
  };

  const _removeMember = (memberId: string) => {
    setSystemMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const members = systemMembers.filter((m) => !m.isGuarantor);
  const guarantors = systemMembers.filter((m) => m.isGuarantor);

  const MappingSection = ({
    title,
    items,
  }: {
    title: string;
    items: MappingItem[];
  }) => {
    if (compact) {
      if (items.length === 0) return null;
      return (
        <div className="mb-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
            {title}
          </div>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden bg-white">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs"
              >
                <div className="min-w-0 flex-1 truncate">
                  <span className="font-medium text-gray-900">
                    {item.name}
                  </span>
                  {item.email && (
                    <span className="text-gray-500 ml-1">
                      ({item.email})
                    </span>
                  )}
                  {item.isInquiryPerson && (
                    <span className="text-blue-600 ml-1">Inquiry Lead</span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {item.mappedTo && (
                    <span
                      className="px-1.5 py-0.5 rounded bg-green-100 text-green-800 font-medium"
                      title={`Mapped to ${item.mappedTo.name}`}
                    >
                      AppFolio
                    </span>
                  )}
                  {item.mappedToGroupOccupant && (
                    <span
                      className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-medium"
                      title={`Mapped to AppFolio group occupant ${item.mappedToGroupOccupant.name}`}
                    >
                      AppFolio Group
                    </span>
                  )}
                  {item.isManuallyConfirmed && (
                    <span
                      className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-medium"
                      title="Confirmed via Venmo name match"
                    >
                      Venmo
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">{title}</h3>
      <div className="space-y-4">
        {items.map((item) => (
          <div
            key={item.id}
            ref={(el) => (mappingRowRefs.current[item.id] = el)}
            className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
          >
            <div className="mb-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {item.name}{" "}
                    {item.email && (
                      <span className="text-gray-500 font-normal">
                        ({item.email})
                      </span>
                    )}
                    {item.isInquiryPerson && (
                      <span className="text-blue-600 ml-2">(Inquiry Lead)</span>
                    )}
                  </div>
                </div>
                <div className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {item.isGuarantor ? "👤 Guarantor" : "👥 Tenant"}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-2">
              {isMapped(item) && (
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                  Mapped
                </span>
              )}
              {hasExistingMappings && !isMapped(item) && (
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                  Unmapped
                </span>
              )}
              {item.isManuallyConfirmed && (
                <span
                  className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded"
                  title="This member's name matched a Venmo payment for this listing — confirmed independently of AppFolio mapping"
                >
                  Confirmed via Venmo
                </span>
              )}
            </div>

            {item.mappedTo && (
              <div className="bg-blue-50/20 rounded-md p-2 mb-2 border border-blue-100">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-900">
                      <span className="text-blue-600">🔗</span>{" "}
                      {item.mappedTo.name}
                      {item.mappedTo.email && (
                        <span className="text-gray-500 font-normal truncate max-w-[200px]">
                          ({item.mappedTo.email})
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-[10px] text-gray-500 mt-0.5">
                      <span className="truncate">
                        🏠 {item.mappedTo.apartmentName}
                      </span>
                      <span>
                        📅{" "}
                        {new Date(
                          item.mappedTo.createdAtUtc,
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.autoMapped && (
                      <span className="bg-green-50 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Auto-mapped
                      </span>
                    )}
                    {!readOnly && (
                      <button
                        onClick={() => handleRemoveMapping(item.id)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {item.mappedToGroupOccupant && (
              <div className="bg-sky-50/30 rounded-md p-2 mb-2 border border-sky-100">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-900">
                      <span className="text-sky-600">🔗</span>{" "}
                      {item.mappedToGroupOccupant.name}
                      {item.mappedToGroupOccupant.email && (
                        <span className="text-gray-500 font-normal truncate max-w-[200px]">
                          ({item.mappedToGroupOccupant.email})
                        </span>
                      )}
                      <span className="bg-sky-100 text-sky-700 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                        AppFolio Group
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-[10px] text-gray-500 mt-0.5">
                      <span className="truncate">
                        🏠 {item.mappedToGroupOccupant.apartmentTitle}
                      </span>
                      <span>
                        📅{" "}
                        {new Date(
                          item.mappedToGroupOccupant.createdAtUtc,
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.autoMapped && (
                      <span className="bg-green-50 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                        Auto-mapped
                      </span>
                    )}
                    {!readOnly && (
                      <button
                        onClick={() => handleRemoveMapping(item.id)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!readOnly && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div
                  className={`grid grid-cols-1 gap-3 ${
                    !isMapped(item) && !item.isManuallyConfirmed
                      ? "sm:grid-cols-2"
                      : ""
                  }`}
                >
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {isMapped(item)
                        ? "Change Mapping:"
                        : "Search Appfolio to Map:"}
                    </label>
                    <div className="space-y-3">
                      <input
                        ref={(el) => (inputRefs.current[item.id] = el)}
                        type="text"
                        placeholder="Search Appfolio records..."
                        value={searchQuery[item.id] || ""}
                        onChange={(e) => handleSearch(item.id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      {filteredAppfolioData[item.id]?.length > 0 && (
                        <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                          {filteredAppfolioData[item.id].map((appfolioMember) => (
                            <div
                              key={appfolioMember.id}
                              onClick={() =>
                                handleSelectMapping(item.id, appfolioMember)
                              }
                              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900 text-sm flex items-center gap-1.5">
                                {appfolioMember.name}{" "}
                                {appfolioMember.email && (
                                  <span className="text-blue-600 font-normal ml-1">
                                    ({appfolioMember.email})
                                  </span>
                                )}
                                {appfolioMember.source === "groupOccupant" && (
                                  <span className="bg-sky-100 text-sky-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                                    AppFolio Group
                                  </span>
                                )}
                              </div>
                              {appfolioMember.apartmentName && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {appfolioMember.apartmentName}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Venmo manual confirmation: only relevant while this member has
                      no AppFolio mapping and hasn't already been confirmed via Venmo */}
                  {!isMapped(item) && !item.isManuallyConfirmed && (
                    <div>
                      <label className="block text-xs font-medium text-purple-700 mb-1">
                        Search Venmo to Confirm:
                      </label>
                      <div className="space-y-3">
                        <input
                          ref={(el) => (venmoInputRefs.current[item.id] = el)}
                          type="text"
                          placeholder="Search Venmo payments..."
                          value={venmoSearchQuery[item.id] || ""}
                          onChange={(e) =>
                            handleVenmoSearch(item.id, e.target.value)
                          }
                          disabled={venmoConfirming[item.id]}
                          className="w-full px-3 py-2 border border-purple-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm disabled:opacity-50"
                        />
                        {filteredVenmoCandidates[item.id]?.length > 0 && (
                          <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                            {filteredVenmoCandidates[item.id].map((candidate) => (
                              <div
                                key={candidate.key}
                                onClick={() =>
                                  handleSelectVenmoMapping(item.id, candidate)
                                }
                                className="p-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900 text-sm">
                                  {candidate.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  ${candidate.amount.toFixed(2)}
                                  {candidate.paymentDate &&
                                    ` • ${new Date(candidate.paymentDate).toLocaleDateString()}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    );
  };

  const allMapped = systemMembers.every((member) => isMapped(member));
  const mappedCount = systemMembers.filter(isMapped).length;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center">
          <div className="text-lg text-gray-600">Loading mappings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {emailStatus && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded shadow-lg text-sm font-medium z-50 ${
            emailStatus === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {emailStatus === "success"
            ? "Email sent successfully!"
            : "Failed to send email. Please try again."}
        </div>
      )}
      {saveStatus && (
        <div
          className={`fixed top-4 right-4 px-4 py-2 rounded shadow-lg text-sm font-medium z-50 ${
            saveStatus === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {saveStatus === "success"
            ? "Mappings saved successfully!"
            : "Failed to save mappings!"}
        </div>
      )}
      {!readOnly && (
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Tenant Mapping
          </h2>
          <p className="text-gray-600">
            Map your system members and guarantors to Appfolio records.
          </p>
        </div>
      )}

      {!readOnly && unresolvedGroupSuggestions.length > 0 && (
        <div className="mb-4 space-y-2">
          {unresolvedGroupSuggestions.map((suggestion: any) => (
            <div
              key={suggestion.groupId}
              className="rounded-lg border border-sky-200 bg-sky-50 p-3"
            >
              <p className="text-xs font-semibold text-sky-800">
                Possible AppFolio group not yet linked
              </p>
              <p className="text-xs text-sky-700 mt-0.5">
                Scraped apartment: "{suggestion.apartmentTitle}
                {suggestion.unitName ? ` — ${suggestion.unitName}` : ""}" —
                doesn't match this apartment ("{apartmentName}")
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {(suggestion.occupants || [])
                  .map((o: any) => o.Name || o.name)
                  .join(", ")}
              </p>
              <button
                type="button"
                disabled={linkingGroupId === suggestion.groupId}
                onClick={() => handleLinkUnresolvedGroup(suggestion.groupId)}
                className="mt-2 text-xs font-medium px-3 py-1.5 rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {linkingGroupId === suggestion.groupId
                  ? "Linking..."
                  : "Link this group"}
              </button>
            </div>
          ))}
        </div>
      )}

      {!isFormFilled ? (
        <div className="flex flex-col items-center justify-center p-6 bg-white rounded-xl shadow-sm border border-dashed border-red-200">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h4 className="text-lg font-bold text-gray-900 mb-2">
            Form Not Filled Yet
          </h4>
          <p className="text-gray-500 text-center max-w-sm">
            Tenant and Gurantor Form is not Filled Yet
          </p>
        </div>
      ) : (
        <>
          {compact ? (
            <div className="flex flex-wrap items-center gap-1.5 text-xs mb-2">
              {(() => {
                const status = getMappingStatus();
                return (
                  <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">
                    {status.mapped}/{status.total} mapped
                    {status.unmapped > 0 ? ` · ${status.unmapped} unmapped` : ""}
                  </span>
                );
              })()}
              {(() => {
                const confirmedViaVenmo = systemMembers.filter(
                  (m) => m.isManuallyConfirmed,
                );
                return confirmedViaVenmo.length > 0 ? (
                  <span
                    className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-medium"
                    title={`Confirmed via Venmo: ${confirmedViaVenmo
                      .map((m) => m.name)
                      .join(", ")}`}
                  >
                    {confirmedViaVenmo.length} confirmed via Venmo
                  </span>
                ) : null;
              })()}
              {venmoPayments.length > 0 && (
                <span
                  className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium"
                  title={venmoPayments
                    .map(
                      (p) =>
                        `$${Number(p.amount ?? 0).toFixed(2)}${
                          p.payerName ? ` from ${p.payerName}` : ""
                        }`,
                    )
                    .join(", ")}
                >
                  $
                  {venmoPayments
                    .reduce((sum, p) => sum + Number(p.amount ?? 0), 0)
                    .toFixed(2)}{" "}
                  via Venmo
                </span>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                {(() => {
                  const status = getMappingStatus();
                  return (
                    <div>
                      <div>
                        Progress: {status.mapped} of {status.total} mappings
                        mapped
                      </div>
                      {status.unmapped > 0 && (
                        <div className="text-orange-600">
                          {status.unmapped} member
                          {status.unmapped > 1 ? "s" : ""} without mappings
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              {(venmoPayments.length > 0 ||
                systemMembers.some((m) => m.isManuallyConfirmed)) && (
                <div className="mt-2 bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="text-sm font-medium text-purple-900">
                    Venmo activity for this listing — independent of AppFolio
                    mapping
                  </div>
                  {(() => {
                    const confirmedViaVenmo = systemMembers.filter(
                      (m) => m.isManuallyConfirmed,
                    );
                    return confirmedViaVenmo.length > 0 ? (
                      <div className="text-xs text-purple-800 mt-1">
                        {confirmedViaVenmo.length} member
                        {confirmedViaVenmo.length > 1 ? "s" : ""} confirmed via
                        Venmo name match (
                        {confirmedViaVenmo.map((m) => m.name).join(", ")})
                      </div>
                    ) : null;
                  })()}
                  {venmoPayments.length > 0 ? (
                    <ul className="text-xs text-purple-800 mt-1 space-y-0.5">
                      {venmoPayments.map((p, idx) => (
                        <li key={p.transactionId || idx}>
                          ${Number(p.amount ?? 0).toFixed(2)}
                          {p.payerName ? ` from ${p.payerName}` : ""}
                          {p.paymentDate
                            ? ` on ${new Date(p.paymentDate).toLocaleDateString()}`
                            : ""}{" "}
                          — counts toward the "paid" requirement for the
                          Mandatory Workflow gate.
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs text-purple-700 mt-1">
                      No processed Venmo payments found for this listing yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className={compact ? "" : "mb-4"}>
            {!readOnly && systemMembers.length > 0 && (
              <div
                className={`mt-2 text-sm p-2 rounded font-medium ${(() => {
                  const newMembersCount = systemMembers.filter(
                    (m) => m.id?.toString().startsWith("temp_") && !isMapped(m),
                  ).length;
                  const hasUnsavedChanges = systemMembers.some(
                    (m) => isMapped(m) && m.id?.toString().startsWith("temp_"),
                  );

                  if (newMembersCount > 0 || hasUnsavedChanges) {
                    return "bg-yellow-50 text-yellow-800 border border-yellow-200";
                  } else {
                    return "bg-green-50 text-green-800 border border-green-200";
                  }
                })()}`}
              >
                {(() => {
                  const newMembersCount = systemMembers.filter(
                    (m) => m.id?.toString().startsWith("temp_") && !isMapped(m),
                  ).length;
                  const hasUnsavedChanges = systemMembers.some(
                    (m) => isMapped(m) && m.id?.toString().startsWith("temp_"),
                  );

                  if (newMembersCount > 0 || hasUnsavedChanges) {
                    return `You have unsaved changes. ${newMembersCount > 0 ? `${newMembersCount} new member${newMembersCount > 1 ? "s" : ""} detected. ` : ""}Please save to update the database.`;
                  } else {
                    return "All mappings are saved and up to date.";
                  }
                })()}
              </div>
            )}
          </div>

          {/* Tenants/Members Section */}
          <MappingSection title="Members" items={members} />

          {/* Guarantors Section */}
          <MappingSection title="Guarantors" items={guarantors} />

          {readOnly ? (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-sm text-gray-600">
                {allMapped
                  ? "All mappings mapped!"
                  : `${mappedCount} of ${systemMembers.length} mappings mapped`}
              </div>
            </div>
          ) : (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {allMapped
                    ? "All mappings mapped!"
                    : `${mappedCount} of ${systemMembers.length} mappings mapped`}
                </div>
                <div className="flex gap-3">
                  {shouldEnableSendEmail() && (
                    <button
                      onClick={() => setIsEmailModalOpen(true)}
                      disabled={emailSending}
                      className={`px-6 py-2 rounded font-medium ${
                        emailSending
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "bg-orange-600 hover:bg-orange-700 text-white"
                      }`}
                    >
                      {emailSending ? "Sending..." : "Send Email"}
                    </button>
                  )}
                  <button
                    onClick={handleSaveAllMappings}
                    disabled={saveLoading}
                    className={`px-6 py-2 rounded font-medium transition-colors ${
                      saveLoading
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                    }`}
                  >
                    {saveLoading ? "Saving..." : "Save All Mappings"}
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500 italic">
                Note: Save will store all current mappings (including unmapped
                members).
              </div>
            </div>
          )}
        </>
      )}

      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSend={handleSendEmail}
        tenantEmail={tenantEmail || "tenant@example.com"}
        tenantName={tenantName || "Tenant Group"}
        unmappedMembers={getEmailData().unmappedMembers}
        mappedMembers={getEmailData().mappedMembers}
        loading={emailSending}
      />
    </div>
  );
};

export default TenantMapping;
